import React, { useState } from "react";
import useSWR from "swr";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Building2, ArrowLeft, FileSpreadsheet, Loader2, MapPin, Calendar, ArrowUpRight, Upload } from "lucide-react";
import { Badge as _BadgeUnused } from "@/components/ui/badge";
import { fmtMoney, fmtDate } from "@/lib/format";
import { toast } from "sonner";

const fetcher = (url) => axios.get(url).then(r => r.data);

export default function VendorDetail() {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { data: vendor, mutate } = useSWR(`/vendors/${vendorId}`, fetcher);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [quotationNo, setQuotationNo] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (fileOnly = false) => {
    if (!file) { toast.error("Please choose a file"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("project_name", projectName);
    fd.append("quotation_no", quotationNo);
    if (fileOnly) fd.append("file_only", "true");
    try {
      const { data } = await axios.post(`/vendors/${vendorId}/boqs`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
      });
      if (data.status === "parsed") {
        const method = data.parse_method === "ai" ? "AI" : "Auto";
        if (data.tallies === false) {
          toast.warning(
            `${method} parsed ${data.item_count} items — totals don't tally with the file's declared total (>3% diff). Open the BOQ to review, or re-upload as reference only.`,
            { duration: 8000 }
          );
        } else if (data.parse_method === "ai") {
          toast.success(
            `AI parsed ${data.item_count} items (${data.ai_confidence || "medium"} confidence) · subtotal ${fmtMoney(data.subtotal)}`,
            { duration: 6000 }
          );
        } else {
          toast.success(`Parsed ${data.item_count} items · subtotal ${fmtMoney(data.subtotal)}`);
        }
      } else {
        toast.info(data.message || "File stored for reference. Open the BOQ to view the raw file.", { duration: 7000 });
      }
      setUploadOpen(false);
      setFile(null); setProjectName(""); setQuotationNo("");
      mutate();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.[0]?.msg || e.message || "Upload failed");
      toast.error(msg.slice(0, 240));
    } finally { setUploading(false); }
  };

  if (!vendor) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const backTo = vendor.project_id ? `/projects/${vendor.project_id}` : "/projects";

  return (
    <div className="min-h-screen">
      <header className="sticky top-3 z-40 mx-auto max-w-6xl px-3 sm:px-4 lg:px-6">
        <div className="glass rounded-full px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Link to={backTo} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary shrink-0">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to project
            </Link>
            <div className="h-4 w-px bg-border/60 mx-1" />
            <div className="flex items-center gap-1.5 min-w-0">
              <Building2 className="h-3.5 w-3.5 text-primary shrink-0" />
              <span className="font-heading text-sm font-bold truncate">{vendor.name}</span>
              {vendor.vendor_type && <Badge variant="outline" className="text-[10px] bg-white/60 backdrop-blur ml-1">{vendor.vendor_type}</Badge>}
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <MapPin className="h-3 w-3" /> {vendor.city}
            {vendor.contact_email && <span>· {vendor.contact_email}</span>}
            {vendor.contact_phone && <span>· {vendor.contact_phone}</span>}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6">
        {vendor.description && <p className="text-sm text-muted-foreground mb-4 max-w-3xl">{vendor.description}</p>}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-lg font-bold">Bill of Quantities</h2>
            <p className="text-sm text-muted-foreground">Upload a new BOQ (.xlsx) or open an existing one to run market analysis.</p>
          </div>
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-upload-boq"><Upload className="h-4 w-4 mr-1" /> Upload BOQ</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload BOQ for {vendor.name}</DialogTitle>
                <DialogDescription>
                  Excel and PDF quotes are auto-parsed. First a fast heuristic; if that can't map the columns or the totals don't tally, our AI (Claude Sonnet 5) reads the file and reconciles items against the declared grand total. If AI also can't parse it, the file is stored for you to review manually.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>File</Label>
                  <Input type="file" accept=".xlsx,.xls,.pdf,.csv" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="input-boq-file" />
                  <p className="text-[11px] text-muted-foreground mt-1">Excel is parsed; PDF / CSV / .xls are stored as reference files.</p>
                </div>
                <div>
                  <Label>Project name (optional)</Label>
                  <Input value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="e.g. Emirates Hills Villa" data-testid="input-project-name" />
                </div>
                <div>
                  <Label>Quotation reference (optional)</Label>
                  <Input value={quotationNo} onChange={e => setQuotationNo(e.target.value)} placeholder="e.g. P2408-QTN-001" data-testid="input-quotation-no" />
                </div>
              </div>
              <DialogFooter className="gap-2 flex-wrap">
                <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
                <Button variant="outline" onClick={() => handleUpload(true)} disabled={uploading || !file} data-testid="btn-upload-file-only">
                  Upload as reference only
                </Button>
                <Button onClick={() => handleUpload(false)} disabled={uploading || !file} data-testid="btn-submit-upload">
                  {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading…</> : "Upload & AI-parse"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {(vendor.boqs || []).length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-12 text-center">
            <FileSpreadsheet className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No BOQs yet. Upload one to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {vendor.boqs.map(b => (
              <button
                key={b.id}
                onClick={() => navigate(`/boqs/${b.id}`)}
                className="text-left rounded-xl border border-border bg-card hover:border-primary/50 hover:shadow-md p-5 transition group"
                data-testid={`boq-card-${b.id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="outline" className="font-mono text-[10px] mb-2">{b.quotation_no}</Badge>
                    <h3 className="font-heading text-base font-bold group-hover:text-primary transition line-clamp-2">{b.project_name}</h3>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <Calendar className="h-3 w-3" /> {fmtDate(b.uploaded_at)}
                    </div>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition shrink-0" />
                </div>
                <div className="mt-4 pt-4 border-t border-border/60 grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Line items</div>
                    <div className="num font-bold">{b.item_count}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Subtotal</div>
                    <div className="num font-bold">{fmtMoney(b.subtotal)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
